import { useState, useEffect } from "react";
import { Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import RefreshButtonTable from "../../../common/RefreshButtonTable";
import TableError from "../../common/TableError";
import TableSkeleton from "../../common/TableSkeleton";
import TablePagination from "../../TeacherAdmin/pagination/TablePagination";
import { useImportLogsHistory } from "../../../../hooks/import/school/useImportLogsHistory";
import { ITEMS_PER_PAGE } from "../../../../constant/constant";
import "../../../../common/styles/table.css";

/**
 * Status badge color mapping
 */
const STATUS_BADGE = {
  completed: { bg: "#ecfdf3", color: "#027a48", label: "Completed" },
  failed: { bg: "#fef3f2", color: "#b42318", label: "Failed" },
  processing: { bg: "#fffaeb", color: "#b54708", label: "Processing" },
  partial: { bg: "#eff8ff", color: "#175cd3", label: "Partial" },
  pending: { bg: "#f9fafb", color: "#344054", label: "Pending" },
};

/**
 * TeacherImportHistory - Displays teacher import logs with table and pagination
 * @param {Object} props
 * @param {"school-admin" | "teacher"} props.type - type of import history (school-admin or teacher)
 */
const TeacherImportHistory = ({ type }) => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);

  const { logs, meta, loading, error, getImportLogs } = useImportLogsHistory();

  useEffect(() => {
    getImportLogs({
      page: currentPage,
      per_page: ITEMS_PER_PAGE,
      filters: { type: "teachers" },
    });
  }, [currentPage]);

  const handleRefresh = () => {
    getImportLogs({
      page: currentPage,
      per_page: ITEMS_PER_PAGE,
      filters: { type: "teachers" },
    });
  };

  const handleRowClick = (log) => {
    if (type == "school-admin")
      navigate(`/school-admin/import-history/teachers/${log.id}`);
    else navigate(`/teacher/import-history/teachers/${log.id}`);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const totalPages = meta?.last_page || 1;

  // Show skeleton on initial load
  if (loading && logs === null) {
    return (
      <>
        <div className="pt-2">
          <RefreshButtonTable handleRefresh={handleRefresh} loading={loading} />
        </div>
        <TableSkeleton rows={ITEMS_PER_PAGE} columns={6} />
      </>
    );
  }

  return (
    <>
      <div className="pt-2">
        <RefreshButtonTable handleRefresh={handleRefresh} loading={loading} />
      </div>

      {error ? (
        <TableError
          error={error}
          handleRefresh={handleRefresh}
          loading={loading}
        />
      ) : !logs || logs.length === 0 ? (
        <div className="alert alert-info mt-3" role="alert">
          No teacher import history found.
        </div>
      ) : (
        <div className="custom-table-container mt-3">
          <div className="custom-table-wrapper">
            <Table hover className="custom-table">
              <thead className="custom-table-head">
                <tr>
                  <th>#</th>
                  <th>Date</th>
                  <th>Total Rows</th>
                  <th>Success</th>
                  <th>Failed</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log, index) => {
                  const badge =
                    STATUS_BADGE[log.status] || STATUS_BADGE.pending;
                  return (
                    <tr
                      key={log.id}
                      onClick={() => handleRowClick(log)}
                      style={{ cursor: "pointer" }}
                    >
                      <td className="custom-id">
                        {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
                      </td>
                      <td>{formatDate(log.created_at)}</td>
                      <td>{log.total_rows}</td>
                      <td style={{ color: "#027a48" }}>{log.success_count}</td>
                      <td style={{ color: "#b42318" }}>{log.failed_count}</td>
                      <td>
                        <span
                          style={{
                            backgroundColor: badge.bg,
                            color: badge.color,
                            padding: "4px 12px",
                            borderRadius: "16px",
                            fontSize: "13px",
                            fontWeight: 500,
                          }}
                        >
                          {badge.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>

          <TablePagination
            totalPage={totalPages}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            disabled={loading}
          />
        </div>
      )}
    </>
  );
};

export default TeacherImportHistory;
